import type { OpticalMeasureSettings } from './ExpoOpticalMeasure.types'

export const getOpticalMeasureSettings = (shouldDrawDebugShapes: boolean) => {
  const settings = {
    input: {
      pupilDetection: false,
      screenDimming: false,
      screenDimmingFactor: 0.333,
      visionOrientation: 'auto',
    },
    framing: {
      normal: {
        preferredDistanceCm: 50,
        guidanceDistanceToleranceCm: 6,
        captureDistanceToleranceCm: 7,
      },
      vbox: {
        preferredDistanceCm: 35,
        guidanceDistanceToleranceCm: 6,
        captureDistanceToleranceCm: 7,
      },
      targetCenter: {
        x: 0.5,
        y: 0.5,
      },
      // Live: MeasurementFramingGuide reads this group to size the oval from
      // the subject's own measured face, re-projected at preferredDistanceCm.
      faceFit: {
        mode: 'session-calibrated',
        fallbackFaceWidthMm: 160,
        fallbackFaceHeightMm: 213,
        // The oval is the face re-projected at preferredDistanceCm and then
        // scaled by this, so a subject who fills it sits at
        // preferredDistanceCm / paddingFactor. At 1.1 that implied 31.8 cm
        // against a 35 cm target, and subjects settled at 31.97 — the oval is
        // the dominant cue, so padding here is a standing disagreement with the
        // distance band.
        //
        // 0.95 rather than 1.0 because filling is not exact at range: at ×1.1
        // subjects matched the implied distance to 0.15 cm, but at ×1.0 they
        // landed 7% short (32.5 against 35), which reads as 35 cm being near
        // comfortable reach. Under-shooting grows the further out you ask, so
        // this is one step (implies 36.8, expect ~34), not a solve for 35.
        ovalPaddingFactor: 0.95,
        minScaleFactor: 0.85,
        maxScaleFactor: 1.15,
        stableFrameCount: 15,
        timeoutMs: 3000,
      },
      position: {
        guidanceToleranceXFactor: 0.15,
        guidanceToleranceYFactor: 0.15,
        captureToleranceXFactor: 0.25,
        captureToleranceYFactor: 0.25,
        guidanceSizeToleranceFactor: 0.15,
        captureSizeToleranceFactor: 0.25,
      },
      enforcement: {
        guidanceEnabled: false,
        captureEnabled: false,
      },
    },
    geometry: {
      corneaProjectionOffset: 0.012,
      useHorizonLine: true,
      rotationXOffsetDeg: 6,
      eyeBox: {
        paddingX: 130,
        paddingY: 60,
        useScaled: true,
        paddingXFactor: 0.7,
        paddingTopFactor: 0.5,
        paddingBottomFactor: 0.5,
        paddingXMinPx: 40,
        paddingTopMinPx: 28,
        paddingBottomMinPx: 56,
      },
    },
    pd: {
      maxPupilAdjustmentPixels: 20,
      maxPupilAdjustedPdDelta: 10,
    },
    sh: {
      source: 'coreml',
      contour: {
        detectionFrameInterval: 10,
        boxEdgeTolerance: 5,
        contrastAdjustment: 0.66,
        detectsDarkOnLight: true,
        minContourPoints: 10,
        minContourArea: 4000,
        rimProximityFactor: 0.5,
        contourOutlierFactor: 2.0,
        contourSmoothingRadius: 5,
        maximumContourImageDimension: 0,
        rejectBrightContours: true,
        brightContourLumaThreshold: 190,
        brightReflectionSmoothnessThreshold: 0.72,
        brightReflectionAspectRatioThreshold: 1.8,
        lumaSampleBoxFactor: 0.45,
        rawFallbackBandFactor: 1.35,
        rawLateralPenalty: 0.25,
        rawBottomPointFraction: 0.05,
        minDownwardOffsetFactor: 0.03,
        outlierMinDeviationPx: 2,
      },
      lowerArcFit: {
        enabled: true,
        minPoints: 8,
        bandFactor: 1.15,
        residualFactor: 0.08,
        fitPointFraction: 0.45,
      },
      smoothing: {
        rimSmoothing: 0.4,
        persistenceFrames: 3,
      },
      gates: {
        maxDifferenceMm: 2.5,
        plausibleMinMm: 8,
        plausibleMaxMm: 40,
        glassesPresenceMinConfidence: 0.4,
        eyeOpenMin: 0.5,
        maxRotationXDeg: 3,
        maxRotationYDeg: 8,
      },
      sharedContourCorrection: {
        factor: 0.08,
        maxMm: 2.5,
      },
      scale: {
        vboxMm: 0,
        trustRatioDeviation: 0.35,
        maxScaleRatioDeviation: 0.6,
      },
      estimate: {
        formula: 'standard',
        toleranceMm: 8,
        scoreExponent: 2,
      },
      calibration: {
        sweepCount: 20,
        sweepStep: 0.03,
        confidenceDropRecalibrationEnabled: true,
        minCandidateConfidence: 0.45,
        confidenceDropRecalibrationFrames: 8,
        confidenceDropMaxRecalibrations: 2,
        narrowSweepCount: 7,
        narrowSweepStep: 0.04,
      },
      coreml: {
        modelVariant: 'medium_512',
        finalFrameModelVariant: 'medium_512',
        cropPaddingXFactorCap: 300,
        cropPaddingTopFactorCap: 250,
        cropPaddingBottomFactorCap: 300,
        frameInterval: 10,
        cropTopExpansionFactor: 0.4,
        maskThreshold: 0.5,
        subpixelRefinement: true,
      },
      capture: {
        eyeOpenThreshold: 0.65,
        targetCandidateFrames: 9,
        multiFrameMedianEnabled: true,
        medianQualityWeightExponent: 1,
        finalFrameMedianWeight: 1,
        spreadMetric: 'mad',
        maxSampleSpreadMm: 1.5,
        maxSampleDispersionMm: 1.0,
      },
      correction: {
        pantoscopicTiltDeg: 8,
        includeHeadPitch: true,
        maxCorrectionFactor: 1.05,
        lensDepthMm: 0,
      },
      review: {
        enabled: true,
        zoomScale: 1.8,
        paddingFactor: 0.3,
        animationMs: 420,
        verticalOffsetFactor: -0.3,
      },
      scoring: {
        qualityWeights: {
          contour: 40,
          symmetry: 30,
          pose: 20,
          scale: 10,
          distance: 20,
          estimate: 20,
        },
        jaggednessWeight: 1.2,
        convexityWeight: 1.0,
        compactnessWeight: 0.5,
        shapeScale: 0.05,
        continuityWeight: 0.5,
        candidateHysteresis: 1.2,
        contourHierarchyDepthPenalty: 0.82,
        aspectRatioBonusThreshold: 1.2,
        aspectRatioBonusSlope: 0.08,
        maxAspectBonus: 0.25,
        lowAspectPenalty: 0.8,
        spanTargetMin: 0.25,
        spanTargetMax: 2.2,
        spanTooSmall: 0.15,
        goodSpanBonus: 1.15,
        narrowSpanPenalty: 0.65,
        wideSpanPenalty: 0.85,
        distanceMinSeparationFactor: 0.15,
        downwardWeight: 0.6,
        downwardMaxBonus: 0.35,
        lateralMinFactor: 0.55,
        lateralPenalty: 0.35,
        partialConfidenceFactor: 0.45,
        symmetryToleranceFactor: 0.15,
        pairConfidenceWeight: 0.8,
        symmetryConfidenceWeight: 0.2,
        scoreConfidenceLogDivisor: 4,
        confidenceScoreWeight: 0.55,
        confidenceShapeWeight: 0.3,
        confidenceFitWeight: 0.15,
        rawFitConfidence: 0.8,
        arcFitFallbackConfidence: 0.5,
        fitResidualConfidenceScale: 4,
        minFitConfidence: 0.2,
        acceptanceQualityThreshold: 0.12,
        qualityPoseToleranceDeg: 7,
        qualityDistanceToleranceCm: 7,
        qualityScaleToleranceDeviation: 0.2,
        qualityRejectedCap: 0.25,
        qualityLateralScoreFactor: 0.4,
        qualityBottomClusterMinCount: 2,
        qualityBottomClusterWidthMinFactor: 0.02,
        qualityLateralDriftMaxFactor: 0.5,
        qualityTinyWidthMinFactor: 0.1,
        qualityWidthIdealMinFactor: 0.25,
        qualityWidthIdealMaxFactor: 0.9,
        qualityTinyHeightMinFactor: 0.07,
        qualityHeightIdealMinFactor: 0.18,
        qualityHeightIdealMaxFactor: 0.75,
        qualityHugeWidthMaxFactor: 1.35,
        qualityHugeHeightMaxFactor: 1.2,
        qualityVerticalSpanMinFactor: 0.06,
        qualityVerticalSpanScoreMinFactor: 0.08,
        qualityVerticalSpanIdealMinFactor: 0.16,
        qualityVerticalSpanIdealMaxFactor: 0.65,
        qualityVerticalSpanScoreMaxFactor: 0.95,
        calibrationAspectRatioThreshold: 2,
        calibrationAspectBonus: 40,
        calibrationSpanMin: 0.3,
        calibrationSpanMax: 0.8,
        calibrationSpanBonus: 20,
        calibrationShapeWeight: 5,
        calibrationCompactnessWeight: 2,
        calibrationBothEyesBonus: 100,
        calibrationBothEyesQualityScale: 15,
        calibrationSymmetryToleranceFactor: 0.1,
        calibrationSymmetryBonus: 50,
        calibrationOtherContourPenalty: 5,
      },
    },
    hsa: {
      pose: {
        minSideRotationY: 65,
        maxSideRotationY: 90,
        maxPitchRotationX: 25,
      },
      detector: {
        detectionFrameInterval: 6,
        freezeSideFrameOnPose: true,
        roiWidthFactor: 1.25,
        roiHeightFactor: 1.6,
        roiCenterXOffsetFactor: 0.15,
        roiCenterYOffsetFactor: 0.05,
        boxEdgeTolerance: 5,
        contrastAdjustment: 2.0,
        detectsDarkOnLight: true,
        maximumContourImageDimension: 0,
        minContourPoints: 8,
        minContourArea: 80,
        candidateMinConfidence: 0.2,
      },
    },
    draw: {
      debugShapesEnabled: shouldDrawDebugShapes,
      shQualityBoundsGuideEnabled: false,
      shQualityHeightGuideEnabled: false,
      shQualitySpanGuideEnabled: false,
      shQualityLateralGuideEnabled: false,
      shQualityEyeDistanceGuideEnabled: false,
    },
  } as const satisfies OpticalMeasureSettings
  return settings
}
